import type { AdditionalNotesType, BatchMedRequest, Medication } from '@/types/data'
import { create } from 'zustand'
import { getCurrentUKDate, isMpsTreatmentLimitReached } from '../utils'
import { createMultiProductOrderSlice, type MultiProductOrderSlice } from './multi-product-order-slice'

export type { DecisionPanelTarget, MultiProductOrderTreatment, TreatmentDecision } from './multi-product-order-slice'

export type SCRData = {
  date: string
  observation?: string
  medication_time?: string
  dosage?: string
  quantity?: string
}

/**
 * A treatment added on top of the one the med request was raised for. Only MPS (MultiProduct
 * Subscription) orders can have these — see `isMpsMedRequest`. Treatment 1 is NOT stored here;
 * it stays on the flat `medication` / `medicationDirection` / `medicationIssueDate` fields so
 * that approve, the footer gating and the post-order view keep reading exactly what they do today.
 */
export type ExtraTreatment = {
  id: string
  medication: Medication | null
  direction: string
  issueDate: Date
  /**
   * The subscription's own order item this card represents. Known only for cards seeded from
   * the family (`seedMpsTreatments`); a card whose add landed via sync keeps null — the add
   * response carries no item hash, only the created medication order's hash
   * (`persistedOrderHash`), which the plan resolves back to an item via the family.
   */
  itemHash?: string | null
  /**
   * Hash of the medication order the add response reported for this card — unique per
   * medication, unlike the 'mps' rows' orderHash (that one is the subscription hash for every
   * mirrored row, so seeding never fills this). Lets the plan pin the card to its exact family
   * row when `itemHash` is unknown.
   */
  persistedOrderHash?: string | null
  /**
   * Med request id of the medication order a landed add created — kept so a retry after a
   * partial sync failure can approve that row without refetching the family (the retry's sync
   * no longer sends the add, so its response id would otherwise be lost).
   */
  persistedMedReqId?: string | null
  /**
   * The medication actually on the subscription — the card's diff baseline. Set at seed time
   * and whenever an approve attempt syncs this card (see useSyncMpsTreatmentsOnApprove); picks
   * stay local until then. Differing from `medication` is what makes the sync swap this item;
   * absent means the card was added locally and the sync must add it to the subscription.
   */
  persistedMedication?: Medication
}

/**
 * A seeded card the prescriber deleted. Nothing is removed from the subscription at delete
 * time — the approve sync processes this queue (first, so a re-picked product doesn't trip the
 * API's duplicate reject) and unqueues each entry as its removal lands.
 */
export type RemovedTreatment = {
  itemHash: string | null
  /** The deleted card's `persistedOrderHash`, kept for the same item resolution. */
  orderHash: string | null
  medication: Medication
}

type SidebarView = 'navigation-menu' | 'contact-patient' | 'rejection' | 'approval-reason'

type Status = 'pending' | 'approved' | 'rejected' | 'on-hold'

export interface MedicationRequestStore extends MultiProductOrderSlice {
  batchOrders: BatchMedRequest[]
  setBatchOrders: (orders: BatchMedRequest[]) => void
  batchOrderData: Record<string, { notes: AdditionalNotesType }>
  setBatchOrderData: (data: Record<string, { notes: AdditionalNotesType }>) => void
  medication: Medication | null
  setMedication: (medication: Medication | null) => void
  medicationDirection: string
  setMedicationDirection: (medicationDirection: string) => void
  medicationIssueDate: Date
  setMedicationIssueDate: (issueDate: Date) => void
  /** Sent with the product change on approve — non-MPS only, MPS swaps go through the subscription sync. */
  isTemporaryReplacement: boolean
  setIsTemporaryReplacement: (isTemporaryReplacement: boolean) => void
  /** LINK orders only. */
  isLastSubscriptionOrder: boolean
  setIsLastSubscriptionOrder: (isLastSubscriptionOrder: boolean) => void
  extraTreatments: ExtraTreatment[]
  addExtraTreatment: () => void
  updateExtraTreatment: (id: string, patch: Partial<Omit<ExtraTreatment, 'id'>>) => void
  removeExtraTreatment: (id: string) => void
  removedTreatments: RemovedTreatment[]
  unqueueRemovedTreatment: (removed: RemovedTreatment) => void
  /** Guards re-seeding on refetches of the same request; reset clears it for the next one. */
  mpsSeededRequestId: string | null
  seedMpsTreatments: (requestId: string, treatments: ExtraTreatment[]) => void
  justification: string
  setJustification: (justification: string) => void
  notes: AdditionalNotesType
  setNotes: (notes: AdditionalNotesType) => void
  sidebarView: SidebarView
  setSidebarView: (sidebarView: SidebarView) => void
  status: Status
  setStatus: (status: Status) => void
  /** True while an approve submit is in flight — the MPS sync plus the family action chain. */
  isApproveSubmitting: boolean
  setApproveSubmitting: (isApproveSubmitting: boolean) => void
  /** True while a Reject (all) / Follow Up chain is in flight. */
  isRejectOrFollowUpSubmitting: boolean
  setRejectOrFollowUpSubmitting: (isRejectOrFollowUpSubmitting: boolean) => void
  selectedBatchOrderIds: string[]
  setSelectedBatchOrderIds: (items: string[]) => void
  rejectionReason: string
  setRejectionReason: (rejectionReason: string) => void
  contactMessage: string
  setContactMessage: (contactMessage: string) => void
  safeguardingQuestions: Record<string, string>
  setSafeguardingQuestions: (payload: Record<string, string>) => void
  sessionStartTime: Date
  resetSessionStartTime: () => void
  scrDataAccordionValue?: string
  setScrDataAccordionValue: (value?: string) => void
}

export const useMedicationRequestStore = create<MedicationRequestStore>((set, get, store) => ({
  ...createMultiProductOrderSlice(set, get, store),
  scrDataAccordionValue: undefined,
  setScrDataAccordionValue: (value?: string) => set({ scrDataAccordionValue: value }),
  batchOrders: [],
  setBatchOrders: (orders: BatchMedRequest[]) => set({ batchOrders: orders }),
  batchOrderData: {},
  setBatchOrderData: (data: Record<string, { notes: AdditionalNotesType }>) => set({ batchOrderData: data }),
  medication: null,
  setMedication: (medication: Medication | null) => set({ medication }),
  medicationDirection: '',
  setMedicationDirection: (medicationDirection: string) => set({ medicationDirection }),
  medicationIssueDate: getCurrentUKDate(),
  setMedicationIssueDate: (issueDate: Date) => set({ medicationIssueDate: issueDate }),
  isTemporaryReplacement: false,
  setIsTemporaryReplacement: (isTemporaryReplacement: boolean) => set({ isTemporaryReplacement }),
  isLastSubscriptionOrder: false,
  setIsLastSubscriptionOrder: (isLastSubscriptionOrder: boolean) => set({ isLastSubscriptionOrder }),
  extraTreatments: [],
  addExtraTreatment: () =>
    set(state => {
      if (isMpsTreatmentLimitReached(state.extraTreatments)) return state

      return {
        extraTreatments: [
          ...state.extraTreatments,
          { id: crypto.randomUUID(), medication: null, direction: '', issueDate: getCurrentUKDate() }
        ]
      }
    }),
  updateExtraTreatment: (id: string, patch: Partial<Omit<ExtraTreatment, 'id'>>) =>
    set(state => ({
      extraTreatments: state.extraTreatments.map(treatment =>
        treatment.id === id ? { ...treatment, ...patch } : treatment
      )
    })),
  // Deleting a card that exists on the subscription queues its baseline for the approve sync
  // instead of calling the API — nothing reaches the subscription before approve.
  removeExtraTreatment: (id: string) =>
    set(state => {
      const removed = state.extraTreatments.find(treatment => treatment.id === id)

      return {
        extraTreatments: state.extraTreatments.filter(treatment => treatment.id !== id),
        removedTreatments: removed?.persistedMedication
          ? [
              ...state.removedTreatments,
              {
                itemHash: removed.itemHash ?? null,
                orderHash: removed.persistedOrderHash ?? null,
                medication: removed.persistedMedication
              }
            ]
          : state.removedTreatments
      }
    }),
  removedTreatments: [],
  unqueueRemovedTreatment: (removed: RemovedTreatment) =>
    set(state => ({ removedTreatments: state.removedTreatments.filter(entry => entry !== removed) })),
  mpsSeededRequestId: null,
  seedMpsTreatments: (requestId: string, treatments: ExtraTreatment[]) =>
    set({ mpsSeededRequestId: requestId, extraTreatments: treatments, removedTreatments: [] }),
  justification: '',
  setJustification: (justification: string) => set({ justification }),
  notes: {
    pharmacist: '',
    customerService: '',
    afterCare: '',
    clinician: ''
  },
  setNotes: (notes: AdditionalNotesType) => set({ notes }),
  sidebarView: 'navigation-menu',
  setSidebarView: (sidebarView: SidebarView) => set({ sidebarView }),
  status: 'pending',
  setStatus: (status: Status) => set({ status }),
  isApproveSubmitting: false,
  setApproveSubmitting: (isApproveSubmitting: boolean) => set({ isApproveSubmitting }),
  isRejectOrFollowUpSubmitting: false,
  setRejectOrFollowUpSubmitting: (isRejectOrFollowUpSubmitting: boolean) => set({ isRejectOrFollowUpSubmitting }),
  selectedBatchOrderIds: [],
  setSelectedBatchOrderIds: (items: string[]) => set({ selectedBatchOrderIds: items }),
  rejectionReason: '',
  setRejectionReason: (rejectionReason: string) => set({ rejectionReason }),
  contactMessage: '',
  setContactMessage: (contactMessage: string) => set({ contactMessage }),
  safeguardingQuestions: {},
  setSafeguardingQuestions: (payload: Record<string, string>) => set({ safeguardingQuestions: payload }),
  sessionStartTime: new Date(),
  resetSessionStartTime: () => set({ sessionStartTime: new Date() })
}))

/** Whether any decision chain (approve, reject / follow up, Submit decision) is in flight. */
export const selectIsDecisionSubmitting = (state: MedicationRequestStore) =>
  state.isApproveSubmitting || state.isRejectOrFollowUpSubmitting || state.isTreatmentDecisionSubmitting

export const resetMedicationRequestStore = () =>
  useMedicationRequestStore.setState({
    ...useMedicationRequestStore.getInitialState(),
    medicationIssueDate: getCurrentUKDate(),
    sessionStartTime: new Date()
  })
